import type { ReactNode } from 'react'

import { Icon, type IconName } from './Icon'

/**
 * The outcome screen shape the design reuses five times: deposit succeeded
 * (`202:1756`), payment failed (`202:1858`), withdrawal queued (`202:2012`),
 * trade completed (`202:1934`) and KYC rejected (`202:1831`).
 *
 * All five are an 80px tinted circle, a 24/Bold title, an optional body, a
 * radius-16 summary card and up to two stacked buttons. Only the tone and the
 * contents change, so the geometry lives here once.
 */
export type ResultTone = 'success' | 'danger' | 'primary' | 'neutral'

const TONE: Record<ResultTone, { ring: string; icon: string }> = {
  success: { ring: 'border-success/40 bg-success/12', icon: 'text-success' },
  danger: { ring: 'border-danger/40 bg-danger/12', icon: 'text-danger' },
  primary: { ring: 'border-primary-glow/35 bg-primary-glow/12', icon: 'text-primary-glow' },
  // An outcome that is neither a success nor a fault: a withdrawal the user
  // cancelled, one whose code was never entered, one released back.
  neutral: { ring: 'border-line-16 bg-surface-8', icon: 'text-ink-80' },
}

interface ResultScreenProps {
  tone?: ResultTone
  icon?: IconName
  title: string
  body?: ReactNode
  /** The headline figure, where the screen has one. */
  headline?: ReactNode
  children?: ReactNode
  actions?: ReactNode
}

export function ResultScreen({
  tone = 'success',
  icon = 'check',
  title,
  body,
  headline,
  children,
  actions,
}: ResultScreenProps) {
  return (
    <div className="sx-col-app flex flex-1 flex-col gap-6 pt-9 pb-6">
      <div className="flex flex-col items-center gap-[22px]">
        <span
          className={`flex size-20 items-center justify-center rounded-full border ${TONE[tone].ring}`}
        >
          <Icon name={icon} size={32} className={TONE[tone].icon} />
        </span>

        <div className="flex flex-col items-center gap-3">
          <h1 className="text-center text-24 leading-8 font-bold text-ink">{title}</h1>
          {headline}
          {body ? (
            <p className="text-center text-14 leading-[25px] text-ink-80">{body}</p>
          ) : null}
        </div>
      </div>

      {children}

      {actions ? <div className="mt-auto flex flex-col gap-4">{actions}</div> : null}
    </div>
  )
}

/** The radius-16 label/value card every outcome screen carries. */
export function ResultSummary({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-16 border border-surface-10 bg-surface-3 p-[15px]">{children}</div>
  )
}
